import React from 'react';
import styles from './AppointmentOrderComplete.module.scss';
import moment from 'moment/moment';
// Fallback when the customer's purchased Option doesn't have an image URL
// stored (or it points at the "missing.png" Paperclip placeholder).
import seniorPhotoFallback from '../schedule-appointment/modesto-senior-portrait-photographer-appointment.jpg';

// `moment.utc(...)` reads stored datetimes' digits as UTC, which matches the
// legacy "Pacific wall-clock digits labeled as UTC" convention used across
// the appointment-time system.
const formatDate = (apptTime) => moment.utc(apptTime).format('dddd, MMMM D, YYYY');
const formatTime = (apptTime) => moment.utc(apptTime).format('h:mm A');

// Polished senior-only confirmation layout. Mirrors the styling on the
// /seniors landing, session-selection, and review pages — sky-blue accents,
// Plus Jakarta Sans, definition-list detail rows with icons. Returned for
// students in grade 12; everyone else falls through to the legacy layout
// below.
function SeniorAppointmentComplete({ orderPackage, order }) {
  const { student, apiOption } = orderPackage;
  const sessionName = apiOption?.name?.replace(/^[A-Z]-\s*/, '') || 'Session';
  // Match the photo to the session the customer purchased — same image used
  // on the session-selection card. Falls back to a generic senior photo if
  // the option has no image (or has the Paperclip missing.png placeholder).
  const sessionPhoto =
    apiOption?.imageUrl && !apiOption.imageUrl.includes('missing.png')
      ? apiOption.imageUrl
      : seniorPhotoFallback;
  const fullName = `${student.firstName} ${student.lastName}`;
  // Senior portrait shoots have a dedicated `senior_location` on the school
  // (used when seniors are photographed at a different room/space than the
  // rest of the school's appointments). Fall back to the regular
  // `schedule_location` when senior_location isn't set.
  const school = student.school;
  const location = school?.seniorLocation || school?.scheduleLocation || '';

  return (
    <div className={styles.seniorWrap}>
      <div className={styles.seniorTopCol}>
        <div className={styles.seniorSuccess} role="status">
            <svg className={styles.seniorSuccessIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
              <polyline points="8 12.5 11 15.5 16.5 9.5" />
            </svg>
            <div>
              <h1 className={styles.seniorHeading}>
                {student.firstName}'s senior portrait session is booked.
              </h1>
              <p className={styles.seniorSuccessMessage}>
                We can't wait to capture this milestone with {student.firstName}.
              </p>
              {order?.id && (
                <p className={styles.seniorOrderId}>Order #{order.id}</p>
              )}
            </div>
          </div>

          <div className={styles.seniorCard}>
        <p className={styles.seniorEyebrow}>Senior Portrait Session</p>
        <h2 className={styles.seniorSessionName}>{sessionName}</h2>

        <div className={styles.seniorCardBody}>
        <dl className={styles.seniorDetails}>
          <div className={styles.seniorDetail}>
            <dt className={styles.seniorDetailLabel}>
              <svg className={styles.seniorDetailIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              Student
            </dt>
            <dd className={styles.seniorDetailValue}>{fullName}</dd>
          </div>
          <div className={styles.seniorDetail}>
            <dt className={styles.seniorDetailLabel}>
              <svg className={styles.seniorDetailIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <line x1="3" y1="10" x2="21" y2="10" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="16" y1="2" x2="16" y2="6" />
              </svg>
              Date
            </dt>
            <dd className={styles.seniorDetailValue}>{formatDate(orderPackage.appointmentTime)}</dd>
          </div>
          <div className={styles.seniorDetail}>
            <dt className={styles.seniorDetailLabel}>
              <svg className={styles.seniorDetailIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="9" />
                <polyline points="12 7 12 12 15 14" />
              </svg>
              Time
            </dt>
            <dd className={styles.seniorDetailValue}>{formatTime(orderPackage.appointmentTime)}</dd>
          </div>
          <div className={styles.seniorDetail}>
            <dt className={styles.seniorDetailLabel}>
              <svg className={styles.seniorDetailIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
              Location
            </dt>
            <dd className={styles.seniorDetailValue}>
              {student.school?.name}
              {location && <span className={styles.seniorDetailSub}>{location}</span>}
            </dd>
          </div>
        </dl>
          <div className={styles.seniorThumbFrame}>
            <img
              className={styles.seniorThumb}
              src={sessionPhoto}
              alt={`${sessionName} — Shoob Photography`}
            />
          </div>
        </div>
        </div>
      </div>

      {(school?.whatToWear || school?.girlsDressCode || school?.boysDressCode || school?.seniorSessionExtraInformation) && (
        <div className={styles.seniorDressCode}>
          <div className={styles.seniorDressCodeHeader}>
            <svg className={styles.seniorDressCodeIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M16 4l-4 3-4-3" />
              <path d="M3 7l5-3 4 3 4-3 5 3v4l-3 1v9H6v-9l-3-1z" />
            </svg>
            <h2 className={styles.seniorDressCodeHeading}>What to Wear</h2>
          </div>
          {school.whatToWear && (
            <p className={styles.seniorDressCodeText}>{school.whatToWear}</p>
          )}
          {(school.girlsDressCode || school.boysDressCode) && (
            <div className={styles.seniorDressCodeGrid}>
              {school.girlsDressCode && (
                <div className={styles.seniorDressCodeCol}>
                  <p className={styles.seniorDressCodeColLabel}>Girls</p>
                  <p className={styles.seniorDressCodeColValue}>{school.girlsDressCode}</p>
                </div>
              )}
              {school.boysDressCode && (
                <div className={styles.seniorDressCodeCol}>
                  <p className={styles.seniorDressCodeColLabel}>Boys</p>
                  <p className={styles.seniorDressCodeColValue}>{school.boysDressCode}</p>
                </div>
              )}
            </div>
          )}
          {school.seniorSessionExtraInformation && (
            <p className={styles.seniorDressCodeNote}>{school.seniorSessionExtraInformation}</p>
          )}
        </div>
      )}

      <a href="/seniors#faq" className={styles.seniorFaqBtn}>
        Got Questions? Read Our Senior FAQ
      </a>

      <p className={styles.seniorContactHelp}>
        Need to reschedule? Contact us at (209) 567-0748 at least 48 hours before your appointment.
      </p>
    </div>
  );
}

export default function AppointmentOrderComplete({ order }) {
  return (
    <div>
      {order
        .cart
        .orderPackages
        .filter((orderPackage) => !!orderPackage.appointmentTime)
        .map((orderPackage) => {
          // Senior flow gets the polished layout; everything else falls
          // through to the legacy elementary/retake confirmation card.
          // Detection uses the Package's `is_senior_portraits` flag (see
          // `Package#senior_portraits?` in the Rails model) rather than
          // `student.grade === '12'` — students booking senior portraits
          // aren't always grade 12 in the data (juniors get pre-photographed,
          // grade fields can be stale, etc.).
          if (orderPackage.package?.isSeniorPortraits) {
            return (
              <SeniorAppointmentComplete
                key={orderPackage.id}
                orderPackage={orderPackage}
                order={order}
              />
            );
          }

          const portraitType = 'portraits';
          const appointmentMomentUTC = moment.utc(orderPackage.appointmentTime);
          const displayDate = appointmentMomentUTC.format('MMMM DD, YYYY');
          const displayTime = appointmentMomentUTC.format('h:mm A');

          return (
            <div className={styles.container} key={orderPackage.id}>
              <h1>{`${orderPackage.student.firstName}'s ${portraitType} appointment is booked!`}</h1>
              <p className={styles.milestoneMsg}>We look forward to capturing this milestone!</p>

              <p className={styles.bold}>{`${orderPackage.student.firstName} ${orderPackage.student.lastName}`}</p>
              <p>Date: <span className={styles.bold}>{displayDate}</span></p>
              <p>Time: <span>{displayTime}</span></p>
              <p>Location: <span className={styles.bold}>{orderPackage.schoolId == 1075 ? 'Shoob Studio 4640 Spyres Way #1, Modesto, CA' : orderPackage.student.school.scheduleLocation}</span></p>
              <p>Session: <span className={styles.bold}>{orderPackage.apiOption.name}</span></p>
            </div>
          );
        })
      }
    </div>
  );
}
